import React from "react";
import { Brand } from "../services/accountService";

interface BrandSelectorProps {
  availableBrands: Brand[];
  selectedBrandIds: string[];
  onBrandToggle: (brandId: string, selected: boolean) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

/**
 * BrandSelector Component
 *
 * Multi-select component for assigning brands to subscription plans.
 * Used in admin UI for managing which brands can offer a specific plan.
 *
 * Features:
 * - Checkbox list of all available brands
 * - Visual feedback for selected brands
 * - Loading state support
 * - At least one brand must be selected
 */
const BrandSelector: React.FC<BrandSelectorProps> = ({
  availableBrands,
  selectedBrandIds,
  onBrandToggle,
  isLoading = false,
  disabled = false,
}) => {
  const isLastSelected = (brandId: string): boolean => {
    return selectedBrandIds.length === 1 && selectedBrandIds.includes(brandId);
  };

  const handleBrandToggle = (brandId: string, checked: boolean) => {
    // Prevent unchecking the last brand
    if (!checked && isLastSelected(brandId)) {
      alert("Każdy plan musi być przypisany do przynajmniej jednego brandu");
      return;
    }

    onBrandToggle(brandId, checked);
  };

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-neutral-900">
        Przypisane Brandy
      </h4>

      <div className="space-y-2">
        {availableBrands.length === 0 ? (
          <div className="text-sm text-neutral-500 italic">
            Brak dostępnych brandów
          </div>
        ) : (
          availableBrands.map((brand) => {
            const isSelected = selectedBrandIds.includes(brand.id);
            const isLast = isLastSelected(brand.id);
            const isDisabled = disabled || isLoading || isLast;

            return (
              <label
                key={brand.id}
                className={`flex items-center space-x-3 p-3 rounded-md border ${
                  isSelected
                    ? "bg-primary-50 border-primary-200"
                    : "bg-white border-neutral-200"
                } ${isDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-neutral-50"}`}
                title={isLast ? "Nie można odznaczyć ostatniego brandu" : ""}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) =>
                    handleBrandToggle(brand.id, e.target.checked)
                  }
                  disabled={isDisabled}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-neutral-900">
                    {brand.displayName}
                  </div>
                  <div className="text-xs text-neutral-500">
                    Slug: {brand.slug}
                  </div>
                </div>
                {isSelected && (
                  <svg
                    className="w-5 h-5 text-primary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </label>
            );
          })
        )}
      </div>

      <div className="text-xs text-amber-600 font-medium mt-2">
        ⚠️ Każdy plan musi być przypisany do przynajmniej jednego brandu
      </div>
    </div>
  );
};

export default BrandSelector;
